import { watch } from 'vue'

interface SEOOptions {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
  author?: string
  keywords?: string
  canonical?: string
  organization?: boolean
  breadcrumbs?: Array<{ name: string; url: string }>
}

export function useSEO() {
  const updateMetaTags = (options: SEOOptions) => {
    const {
      title,
      description,
      image,
      url,
      type = 'website',
      publishedTime,
      modifiedTime,
      author,
      keywords,
      canonical,
      organization = false,
      breadcrumbs
    } = options

    // Actualizar título de la página
    if (title) {
      // Solo agregar "| Fourin Blog" si es un artículo, sino usar el título completo
      document.title = type === 'article' ? `${title} | Fourin Blog` : title
    }

    // Actualizar o crear meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    if (description) {
      metaDescription.setAttribute('content', description)
    }

    // Actualizar o crear keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]')
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta')
        metaKeywords.setAttribute('name', 'keywords')
        document.head.appendChild(metaKeywords)
      }
      metaKeywords.setAttribute('content', keywords)
    }

    // Actualizar o crear canonical URL
    const canonicalUrl = canonical || url || window.location.href
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]')
      if (!canonicalLink) {
        canonicalLink = document.createElement('link')
        canonicalLink.setAttribute('rel', 'canonical')
        document.head.appendChild(canonicalLink)
      }
      canonicalLink.setAttribute('href', canonicalUrl)
    }

    // Actualizar o crear Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'og:type', content: type }
    ]

    ogTags.forEach(({ property, content }) => {
      if (content) {
        let meta = document.querySelector(`meta[property="${property}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('property', property)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      }
    })

    // Actualizar o crear Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image }
    ]

    twitterTags.forEach(({ name, content }) => {
      if (content) {
        let meta = document.querySelector(`meta[name="${name}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('name', name)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      }
    })

    // Actualizar datos estructurados (Schema.org)
    const schemas: any[] = []

    // Schema para artículos
    if (type === 'article' && title) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url || window.location.href
        },
        headline: title,
        image: image || 'https://fourin.co/fourin.png',
        author: {
          '@type': 'Person',
          name: author || 'Fourin'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Fourin',
          logo: {
            '@type': 'ImageObject',
            url: 'https://fourin.co/fourin.png'
          }
        },
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime,
        description: description
      })
    }

    // Schema para Organization (si se solicita)
    if (organization || type === 'website') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Fourin',
        url: 'https://fourin.co',
        logo: 'https://fourin.co/fourin.png',
        description: 'Software CMMS/GMAO líder en Colombia y LATAM para gestión de activos y mantenimiento preventivo',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'CO'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          areaServed: ['CO', 'LATAM'],
          availableLanguage: 'Spanish'
        }
      })
    }

    // Schema para BreadcrumbList
    if (breadcrumbs && breadcrumbs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url
        }))
      })
    }

    // Actualizar o crear scripts de schema
    if (schemas.length > 0) {
      // Eliminar schemas antiguos que no sean del tipo correcto
      const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]')
      existingSchemas.forEach((script, index) => {
        if (index >= schemas.length) {
          script.remove()
        }
      })

      // Crear o actualizar schemas
      schemas.forEach((schema, index) => {
        let schemaScript = existingSchemas[index] as HTMLScriptElement
        if (!schemaScript) {
          schemaScript = document.createElement('script')
          schemaScript.setAttribute('type', 'application/ld+json')
          document.head.appendChild(schemaScript)
        }
        schemaScript.textContent = JSON.stringify(schema)
      })
    }
  }

  const resetMetaTags = () => {
    // Resetear a valores por defecto
    document.title = 'Fourin - Software CMMS/GMAO en Colombia | Gestión de Activos'

    const defaultDescription = 'Software CMMS/GMAO líder en Colombia y LATAM. Fourintrack te ayuda a gestionar activos, mantenimiento preventivo y predictivo. Reduce paradas, costos y riesgos. Prueba gratis 14 días.'

    const defaultKeywords = 'CMMS Colombia, GMAO Latinoamérica, software mantenimiento, gestión de activos, mantenimiento preventivo, software órdenes de trabajo, CMMS en la nube'

    updateMetaTags({
      title: 'Fourin - Software CMMS/GMAO en Colombia | Gestión de Activos',
      description: defaultDescription,
      keywords: defaultKeywords,
      url: 'https://fourin.co',
      canonical: 'https://fourin.co',
      type: 'website',
      organization: true
    })
  }

  return {
    updateMetaTags,
    resetMetaTags
  }
}
